import { useEffect, useState } from 'react'
import { FormProvider, useFieldArray, useForm, type Control, type FieldArrayPath, type Resolver } from 'react-hook-form'
import { useMediaQuery } from '@mui/material'

import { PlusCircleIcon } from '@assets/svgs/PlusCircleIcon'
import { emotionTheme } from '@styles/theme'
import { RowBox } from '@components/shared/RowBox'
import { Button } from '@components/shared/Button'
import { Input } from '@components/shared/Input'
import { CalendarIcon } from '@assets/svgs/CalendarIcon'
import { Tooltip } from '@components/shared/Tooltip'
import { InfoIcon } from '@assets/svgs/InfoIcon'
import { TrashSimple } from '@assets/svgs/TrashSimple'
import { DatePicker } from '@components/shared/DatePicker'
import { BaseModal2 } from '@components/modals/BaseModal2'
import { initialFormValues } from './form/initialFormValues'
import { bulkDistributeBillingModalSchema } from './form/validations'

import type { BulkDistributeBillingModalProps } from './types'
import type { BulkDistributeBillingModalFormProps } from './form/types'

import * as S from './styles'

export const BulkDistributeBillingModal = ({
  open,
  loading,
  onClose,
  onConfirm,
  ...rest
}: BulkDistributeBillingModalProps) => {
  const isMobile = useMediaQuery(`(max-width: ${emotionTheme.screenSizes.laptop})`)

  const methods = useForm<BulkDistributeBillingModalFormProps>({
    defaultValues: initialFormValues,
    resolver: bulkDistributeBillingModalSchema as unknown as Resolver<BulkDistributeBillingModalFormProps>
  })

  const { fields, append, remove, replace } = useFieldArray<BulkDistributeBillingModalFormProps>({
    control: methods.control as Control<BulkDistributeBillingModalFormProps>,
    name: 'holidays' as FieldArrayPath<BulkDistributeBillingModalFormProps>
  })

  const [selectedOption, setSelectedOption] = useState(true)

  const addHoliday = () => append({ date: '' })

  const removeHoliday = (index: number) => remove(index)

  const onSubmit = (formData: BulkDistributeBillingModalFormProps) => {
    onConfirm(!selectedOption ? formData : null)
  }

  const handleResetForm = () => {
    methods.reset(initialFormValues)
    setSelectedOption(true)
    replace([])
  }

  useEffect(() => {
    !open && handleResetForm()
  }, [open])

  useEffect(() => {
    setSelectedOption(!!selectedOption)
    methods.setValue('distributionType', !!selectedOption)
  }, [selectedOption])

  return (
    <BaseModal2
      open={open}
      width={isMobile ? 'calc(100% - 20px)' : 470}
      heightContainer="auto"
      maxHeightContainer={isMobile ? 'calc(100% - 20px)' : '80%'}
      title="Distribuir faturamento"
      headerIcon={<PlusCircleIcon />}
      onClose={onClose}
      footer={{
        loading,
        cancelButtonText: 'Cancelar',
        cancelButtonMinWidth: 'calc(50% - 8px)',
        confirmButtonText: 'Confirmar',
        confirmButtonMinWidth: 'calc(50% - 8px)',
        onConfirm: methods.handleSubmit(onSubmit)
      }}
      {...rest}
    >
      <FormProvider {...methods}>
        <S.Title>
          Selecione a opção desejada sobre a distribuição do faturamento do mês anterior na prévia atual:
        </S.Title>

        <S.MenuItem onClick={() => setSelectedOption(true)}>
          <S.Radio name="distributionType" checked={!!selectedOption} value={true} />
          <S.RadioText>Distribuir nos dias do mês atual</S.RadioText>
        </S.MenuItem>

        <S.MenuItem onClick={() => setSelectedOption(false)}>
          <S.Radio name="distributionType" checked={!selectedOption} value={false} />
          <S.RadioText>
            Distribuir nos dias úteis do mês atual
            <Tooltip
              title="A média diária de faturamento do mês anterior será distribuída automaticamente nos dias úteis do mês atual (segunda a sexta-feira). Se necessário, você pode ajustar os valores manualmente."
              placement="top-end"
            >
              <InfoIcon />
            </Tooltip>
          </S.RadioText>
        </S.MenuItem>

        {!selectedOption && (
          <RowBox marginTop={24}>
            <Input
              name="invoiceDays"
              label="Dias de faturamento do mês anterior"
              placeholder="30 - Qtd de dias"
              rightIcon={<CalendarIcon />}
              maskType="onlyNumbers"
              maxLength={2}
              required
            />
            <Input name="weekdays" label="Segunda a Sexta-feira (%)" placeholder="0%" maskType="percentage" required />
            <Input name="saturday" label="Sábado (%)" placeholder="0%" maskType="percentage" />
            <Input name="sunday" label="Domingo/Feriado (%)" placeholder="0%" maskType="percentage" />

            <Button
              width="100%"
              style={{ marginTop: 16 }}
              colorType="outlineBlue"
              rightIcon={<PlusCircleIcon />}
              onClick={addHoliday}
            >
              Adicionar feriado
            </Button>

            {fields.map((field, index) => (
              <S.DateBox key={field.id}>
                <DatePicker
                  name={`holidays[${index}].date`}
                  label={`Data do feriado - ${index + 1}`}
                  placeholder="DD/MM/AAAA"
                />

                <Tooltip width="min-content" title="Remover feriado" placement="top-end">
                  <Button
                    width={24}
                    colorType="outlineBlue"
                    onClick={() => removeHoliday(index)}
                    rightIcon={<TrashSimple />}
                  />
                </Tooltip>
              </S.DateBox>
            ))}
          </RowBox>
        )}
      </FormProvider>
    </BaseModal2>
  )
}
